import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ApiError, ago, api, atLeast, on, type Bench, type Campaign, type CampaignStop, type Role, type Run } from "../api";
import { Board, type Crumb, Loading, Notice, PageHead, Row, Section, Status } from "../components/Look";
import Modal from "../components/Modal";
import StartTesting from "../components/StartTesting";
import { buildVerdict, failedOf } from "../components/buildState";
import { campaignBuildName } from "../components/campaignBuild";
import { useTitle } from "../useTitle";

/**
 * A product's builds, and one build.
 *
 * THE PAGE THAT WAS MISSING (step K2 of the rebuild). "Is 7.2.72 good?" was
 * answered in three places at once - Now, the campaigns list, a campaign - and
 * completely in none. A build is every run that recorded the same product
 * commit (HIL_BUILD_REF, else HIL_BUILD), with the campaigns those runs belong
 * to; the server groups them, this page reads them.
 */
function useBuilds(pid: number) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.projectBuilds>> | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    api
      .projectBuilds(pid)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [pid]);
  return { data, error };
}

const keyParam = (k: string) => encodeURIComponent(k);

export function Builds() {
  const { t } = useTranslation();
  const pid = Number(useParams().id);
  const { data, error } = useBuilds(pid);
  useTitle(t("builds.listTitle"));
  if (error) return <Notice tone="error">{error}</Notice>;
  if (!data) return <Loading label={t("common.loading")} />;
  const { builds, unbuilt } = data;
  const newest = builds[0];
  return (
    <section>
      <PageHead
        crumbs={[
          { label: data.project?.name ?? t("projects.fallbackTitle", { id: pid }), to: `/projects/${pid}` },
          { label: t("product.tabBuilds") },
        ]}
        title={t("builds.listTitle")}
        answer={
          builds.length
            ? t("builds.listAnswer", { count: builds.length, build: newest.label || newest.key })
            : t("builds.listNone")
        }
        after={<p>{builds.length ? t("builds.listAfter") : t("builds.listNoneAfter")}</p>}
      />
      {builds.length > 0 && (
        <Board as="ul">
          {builds.map((b) => {
            const v = buildVerdict(b);
            return (
              <li key={b.key}>
                <Row
                  status={<Status tone={v.tone}>{v.word}</Status>}
                  title={
                    <Link className="font-semibold hover:underline" to={`/projects/${pid}/builds/${keyParam(b.key)}`}>
                      {b.label || b.key}
                    </Link>
                  }
                  sub={
                    <span>
                      {b.label && b.label !== b.key && <span className="font-mono text-xs">{b.key}</span>}
                      {b.label && b.label !== b.key && " · "}
                      {t("builds.counts", { runs: b.runs.length, campaigns: b.campaigns.length })}
                    </span>
                  }
                  aside={<span className="whitespace-nowrap text-sm text-slate-500">{ago(b.last_at)}</span>}
                />
              </li>
            );
          })}
        </Board>
      )}
      {unbuilt.length > 0 && (
        <Section title={t("builds.unbuiltTitle", { count: unbuilt.length })} note={t("builds.unbuiltNote")}>
          <RunList runs={unbuilt.slice(0, 20)} />
        </Section>
      )}
    </section>
  );
}

export function BuildPage() {
  const { t } = useTranslation();
  const params = useParams();
  const pid = Number(params.id);
  const key = decodeURIComponent(params.key ?? "");
  const { data, error } = useBuilds(pid);
  const [role, setRole] = useState<Role | null>(null);
  const [benches, setBenches] = useState<Bench[]>([]);
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    let alive = true;
    api
      .project(pid)
      .then((d) => {
        if (!alive) return;
        setRole(d.role ?? null);
        setBenches(d.benches);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [pid]);
  const b = data?.builds.find((x) => x.key === key);
  useTitle(b ? t("builds.title", { build: b.label || b.key }) : t("builds.listTitle"));
  if (error) return <Notice tone="error">{error}</Notice>;
  if (!data) return <Loading label={t("common.loading")} />;
  const up: Crumb[] = [
    { label: data.project?.name ?? t("projects.fallbackTitle", { id: pid }), to: `/projects/${pid}` },
    { label: t("product.tabBuilds"), to: `/projects/${pid}/builds` },
  ];
  if (!b) {
    return (
      <section>
        <PageHead crumbs={[...up, { label: key }]} title={t("builds.listTitle")}
                  answer={t("builds.notFound", { build: key })} />
      </section>
    );
  }
  const v = buildVerdict(b);
  const c = b.campaigns[0];
  const outside = b.runs.filter((r) => !r.campaign_id);
  const full = outside.filter((r) => r.scope === "full");
  const slices = outside.filter((r) => r.scope !== "full");
  const showBench = (data.bench_count ?? 2) > 1;
  const mayRun = atLeast(role, "operator") && benches.some((x) => x.has_agent !== false);
  // The bench images this build was tested in: the product build layered with
  // rig scripts and a harness. Folded - a reader asks "which build", rarely
  // "which image".
  const images = [...new Set(b.campaigns.map((x) => x.build_ref).filter(Boolean))];
  // What the newest campaign says case by case: each failure, known or not,
  // with a way to the case's own page.
  const telling = (c?.stops ?? []).flatMap((s) =>
    (s.failed_ids ?? []).map((id) => ({
      id,
      name: s.failed_names?.[id] ?? "",
      stop: s.label || s.profile,
      known: (s.known ?? []).includes(id),
    })),
  ).sort((x, y) => Number(x.known) - Number(y.known));
  return (
    <section>
      <PageHead
        crumbs={[...up, { label: b.label || b.key }]}
        title={t("builds.title", { build: b.label || b.key })}
        answer={v.answer}
        bad={v.tone === "bad"}
        after={
          <p className="text-sm text-slate-500">
            {b.label && b.label !== b.key && <span className="font-mono">{b.key} · </span>}
            {t("builds.span", { runs: b.runs.length, first: on(b.first_at), last: on(b.last_at) })}
          </p>
        }
      >
        <div className="mt-4 flex flex-wrap items-start gap-2">
          {mayRun && (
            <button type="button" className="btn-primary" onClick={() => setStarting(true)}>
              {t("builds.startCampaign")}
            </button>
          )}
          {c && (
            <Link className="btn-quiet" to={`/reports?campaign=${c.id}`}>{t("builds.report")}</Link>
          )}
          {images.length > 0 && (
            <details className="btn-quiet max-w-full">
              <summary className="cursor-pointer">{t("builds.images", { count: images.length })}</summary>
              <ul className="mt-1.5 break-all font-mono text-xs text-slate-600 dark:text-slate-400">
                {images.map((x) => <li key={x}>{x}</li>)}
              </ul>
            </details>
          )}
        </div>
      </PageHead>
      {c && (
        <Section
          card
          title={t("builds.newestCampaign", { id: c.id })}
          note={`${campaignBuildName(c)} · ${on(c.created_at)}`}
          actions={<Link className="btn-quiet" to={`/campaigns/${c.id}`}>{t("builds.openCampaign")}</Link>}
        >
          <Route stops={c.stops} />
        </Section>
      )}
      <div className="grid gap-x-6 lg:grid-cols-2">
        {c && (
          <Section card title={t("builds.telling")} note={t("builds.tellingNote", { id: c.id })}>
            {telling.length ? (
              <Board as="ul">
                {telling.map((x) => (
                  <li key={`${x.stop}/${x.id}`}>
                    <Row
                      status={<Status tone={x.known ? "warn" : "bad"}>{x.known ? t("builds.tellKnown") : t("builds.tellFailed")}</Status>}
                      title={
                        <Link className="hover:underline" to={`/projects/${pid}/cases/${encodeURIComponent(x.id)}`}>
                          <span className="font-mono text-sm font-semibold">{x.id}</span>
                          {x.name && <span className="text-slate-600 dark:text-slate-400"> · {x.name}</span>}
                        </Link>
                      }
                      aside={
                        <Link className="underline" to={`/projects/${pid}/cases/${encodeURIComponent(x.id)}`}>
                          {t("builds.open")}
                        </Link>
                      }
                    />
                  </li>
                ))}
              </Board>
            ) : (
              <p className="text-base text-slate-500">{t("builds.tellingNone")}</p>
            )}
          </Section>
        )}
        {b.campaigns.length > 0 && (
          <Section card title={t("builds.campaigns", { count: b.campaigns.length })}>
            <Board as="ul">
              {b.campaigns.map((x) => (
                <li key={x.id}>
                  <Row
                    status={<Status tone={campTone(x)}>{campWord(x)}</Status>}
                    title={
                      <span>
                        <Link className="font-semibold hover:underline" to={`/campaigns/${x.id}`}>#{x.id}</Link>
                        <span className="text-sm text-slate-500">
                          {" · "}
                          {t(`campaign.origin.${x.origin ?? "manual"}` as "campaign.origin.nightly", { when: ago(x.created_at) })}
                        </span>
                      </span>
                    }
                    aside={<span className="whitespace-nowrap tabular-nums">{t("builds.ranOf", { ran: x.ran, planned: x.planned ?? "?" })}</span>}
                  />
                </li>
              ))}
            </Board>
          </Section>
        )}
      </div>
      {full.length > 0 && (
        <Section card title={t("builds.fullRuns", { count: full.length })}>
          <RunList runs={full} showBench={showBench} />
        </Section>
      )}
      {slices.length > 0 && (
        <Section card title={t("builds.slices", { count: slices.length })} note={t("builds.slicesNote")}>
          <RunList runs={slices} showBench={showBench} />
        </Section>
      )}
      {starting && (
        <Modal wide title={t("builds.startCampaign")} onClose={() => setStarting(false)}>
          <StartTesting projectId={pid} fromProject />
        </Modal>
      )}
    </section>
  );
}

function campTone(c: Campaign) {
  return buildVerdict({ key: "", label: "", first_at: null, last_at: null, runs: [], campaigns: [c] }).tone;
}
function campWord(c: Campaign) {
  return buildVerdict({ key: "", label: "", first_at: null, last_at: null, runs: [], campaigns: [c] }).word;
}

/** A campaign read as a route: its stops in order, each a dot, a name and a count. */
export function Route({ stops }: { stops: CampaignStop[] }) {
  const { t } = useTranslation();
  return (
    <ol className="grid gap-4 py-1 sm:grid-flow-col sm:auto-cols-fr" aria-label={t("builds.route")}>
      {stops.map((s) => {
        const known = (s.known ?? []).length;
        const fresh = Math.max(0, s.failed - known);
        const tone = s.lost || fresh ? "bad" : known || s.skipped || (s.planned !== null && s.ran < s.planned) ? "warn" : s.state === "done" ? "good" : "off";
        const dot = { good: "bg-success-500", warn: "bg-amber-500", bad: "bg-red-500", off: "bg-slate-300 dark:bg-slate-600" }[tone];
        const mark = tone === "good" ? "✓" : s.lost ? "!" : fresh || known || "·";
        const text = s.lost
          ? t("builds.stopLost")
          : `${s.passed} / ${s.planned ?? s.ran}${known ? ` · ${t("builds.stopKnown", { n: known })}` : ""}${fresh ? ` · ${t("builds.stopFailed", { n: fresh })}` : ""}`;
        const body = (
          <>
            <span aria-hidden="true" className={`grid h-8 w-8 place-items-center rounded-full text-sm font-bold text-white ${dot}`}>{mark}</span>
            <span className="text-sm font-medium">{s.label || s.profile}</span>
            <span className="text-xs text-slate-500 tabular-nums">{text}</span>
          </>
        );
        return (
          <li key={s.pos} className="grid justify-items-center gap-1 text-center">
            {s.run_id ? <Link to={`/runs/${s.run_id}`} className="grid justify-items-center gap-1 hover:underline">{body}</Link> : body}
          </li>
        );
      })}
    </ol>
  );
}

function RunList({ runs, showBench = true }: { runs: Run[]; showBench?: boolean }) {
  const { t } = useTranslation();
  return (
    <Board as="ul">
      {runs.map((r) => {
        const f = failedOf(r);
        const tone = r.finished_at == null ? "live" : f ? "bad" : (r.verdict_count ?? 0) ? "good" : "off";
        const word = r.finished_at == null ? t("builds.wRunning") : f ? t("builds.wFailed", { count: f }) : (r.verdict_count ?? 0) ? t("builds.wClean") : t("builds.wNoResults");
        return (
          <li key={r.id}>
            <Row
              status={<Status tone={tone}>{word}</Status>}
              title={<Link className="font-semibold hover:underline" to={`/runs/${r.id}`}>{r.name}</Link>}
              sub={[r.reason, showBench ? r.bench_name : ""].filter(Boolean).join(" · ") || undefined}
              aside={<span className="whitespace-nowrap text-sm text-slate-500">{on(r.started_at)}</span>}
            />
          </li>
        );
      })}
    </Board>
  );
}
